import { useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, MailWarning } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "access", label: "Access my data" },
  { id: "correction", label: "Correct my data" },
  { id: "deletion", label: "Delete my data" },
  { id: "takedown", label: "Takedown request" },
  { id: "other", label: "Something else" },
];

export const LegalRequestForm = () => {
  const [form, setForm] = useState({ email: "", type: "access", message: "" });
  const [state, setState] = useState({ phase: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ phase: "loading", msg: "" });
    try {
      const { data } = await axios.post(`${API}/data-requests`, form);
      setState({ phase: "success", msg: data.message });
      setForm({ email: "", type: "access", message: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      setState({ phase: "error", msg: typeof d === "string" ? d : "Could not send — try again, or email us." });
    }
  };

  return (
    <form data-testid="legal-request-form" onSubmit={submit} className="mt-6 rounded-2xl bg-white border border-black/5 shadow-sm p-6 sm:p-8">
      <p className="flex items-center gap-2.5 font-display font-semibold text-lg">
        <MailWarning size={18} className="text-trust" /> Send a request directly
      </p>
      <p className="mt-2 text-sm text-gray-500">
        Prefer email? Write to <a href="mailto:support@proofbuy.in" className="text-trust font-semibold">support@proofbuy.in</a>.
        Either way, your request lands with the team and we act on it.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <input data-testid="legal-request-email" required type="email" value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="Your email (the one on your account)"
          className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-trust placeholder:text-gray-400" />
        <select data-testid="legal-request-type" value={form.type}
          onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-trust">
          {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      <textarea data-testid="legal-request-message" required minLength={10} maxLength={2000} rows={3}
        value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
        placeholder="Describe your request — for takedowns, include the product/seller name from the review."
        className="mt-4 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-trust resize-none placeholder:text-gray-400" />
      <button data-testid="legal-request-submit" type="submit" disabled={state.phase === "loading"}
        className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-trust text-white font-semibold px-6 py-3 text-sm transition-colors duration-200 hover:bg-trust-dark disabled:opacity-60">
        {state.phase === "loading" ? <Loader2 size={15} className="animate-spin" /> : "Send request"}
      </button>
      {state.msg && (
        <p data-testid="legal-request-message-status" role="status"
          className={`mt-3 flex items-center gap-2 text-sm font-semibold ${state.phase === "success" ? "text-verified" : "text-warn"}`}>
          {state.phase === "success" ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />} {state.msg}
        </p>
      )}
    </form>
  );
};
